import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { ChevronDown, FileText, MessageSquare, Sparkles, Users } from "lucide-react";
import {
  fetchWorkspace,
  generateWorkspaceCopy,
  getAdminUser,
  refreshAdminSession,
  updateWorkspace,
} from "../api/adminClient";
import { uploadCompanyLogo, LOGO_ACCEPT, formatLogoUploadError } from "./uploadCompanyLogo";

const DEFAULT_PRIMARY_COLOR = "#004f73";

function hexForColorInput(value) {
  const raw = String(value || "").trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(raw)) return raw.toLowerCase();
  if (/^[0-9A-Fa-f]{6}$/.test(raw)) return `#${raw.toLowerCase()}`;
  return DEFAULT_PRIMARY_COLOR;
}

function normalizeHexInput(raw) {
  const v = String(raw || "").trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(v)) return v.toLowerCase();
  if (/^[0-9A-Fa-f]{6}$/.test(v)) return `#${v.toLowerCase()}`;
  return v;
}

const CUSTOMIZATION_PANELS = [
  {
    key: "reportIntro",
    title: "PDF cover & intro",
    description: "Cover title, subtitle, and opening page copy on Company, Team, and LITE PDFs.",
    Icon: FileText,
  },
  {
    key: "designations",
    title: "Invite job titles",
    description: "Role options shown in the invite wizard for this workspace.",
    Icon: Users,
  },
  {
    key: "copy",
    title: "Participant messages",
    description: "Welcome and completion copy on the participant assessment journey.",
    Icon: MessageSquare,
  },
];

function buildSnapshot(fields) {
  return JSON.stringify(fields);
}

function fieldsFromWs(ws) {
  const cfg = ws?.report_config || {};
  const des = Array.isArray(ws?.designations) ? ws.designations : [];
  return {
    productName: ws?.product_display_name || ws?.name || "",
    logoUrl: ws?.logo_url || "",
    primaryColor: ws?.primary_color || "",
    footerText: ws?.footer_text || "",
    welcomeMessage: ws?.welcome_message || ws?.participant_copy?.welcome_description || "",
    completedMessage: ws?.completed_message || ws?.participant_copy?.completed_full || "",
    coverTitle: cfg.report_cover_title || "",
    coverSubtitle: cfg.report_cover_subtitle || "",
    introHeading: cfg.report_intro_heading || "",
    introBody: cfg.report_intro_body || "",
    designationsText: [...des]
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
      .map((d) => String(d.label || "").trim())
      .filter(Boolean)
      .join("\n"),
  };
}

export default function WorkspaceBrandingCard({ onSaved }) {
  const session = getAdminUser();
  const isPlatformAdmin = Boolean(session?.is_platform_admin);
  const sessionHasTenant = Boolean(session?.tenant_id) && !isPlatformAdmin;

  const [workspaceReady, setWorkspaceReady] = useState(false);
  const [productName, setProductName] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoBroken, setLogoBroken] = useState(false);
  const [primaryColor, setPrimaryColor] = useState("");
  const [footerText, setFooterText] = useState("");
  const [welcomeMessage, setWelcomeMessage] = useState("");
  const [completedMessage, setCompletedMessage] = useState("");
  const [coverTitle, setCoverTitle] = useState("");
  const [coverSubtitle, setCoverSubtitle] = useState("");
  const [introHeading, setIntroHeading] = useState("");
  const [introBody, setIntroBody] = useState("");
  const [activePanel, setActivePanel] = useState(null);
  const [designationsText, setDesignationsText] = useState("");
  const [loading, setLoading] = useState(sessionHasTenant);
  const [saving, setSaving] = useState(false);
  const [generatingCopy, setGeneratingCopy] = useState(false);
  const [aiBrief, setAiBrief] = useState("");
  const [savedBaseline, setSavedBaseline] = useState(null);

  useEffect(() => {
    if (isPlatformAdmin || !sessionHasTenant) {
      setWorkspaceReady(false);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await fetchWorkspace();
        if (cancelled) return;
        const ws = res.data?.workspace;
        if (!ws) {
          setWorkspaceReady(false);
          return;
        }
        const loaded = fieldsFromWs(ws);
        setProductName(loaded.productName);
        setLogoUrl(loaded.logoUrl);
        setLogoBroken(false);
        setPrimaryColor(loaded.primaryColor);
        setFooterText(loaded.footerText);
        setWelcomeMessage(loaded.welcomeMessage);
        setCompletedMessage(loaded.completedMessage);
        setCoverTitle(loaded.coverTitle);
        setCoverSubtitle(loaded.coverSubtitle);
        setIntroHeading(loaded.introHeading);
        setIntroBody(loaded.introBody);
        setDesignationsText(loaded.designationsText);
        setSavedBaseline(buildSnapshot(loaded));

        if (
          loaded.coverTitle ||
          loaded.coverSubtitle ||
          loaded.introHeading ||
          loaded.introBody
        ) {
          setActivePanel("reportIntro");
        } else if (loaded.designationsText.trim()) {
          setActivePanel("designations");
        } else {
          setActivePanel("copy");
        }
        setWorkspaceReady(true);
      } catch {
        if (!cancelled) setWorkspaceReady(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isPlatformAdmin, sessionHasTenant]);

  const currentFields = useMemo(
    () => ({
      productName,
      logoUrl,
      primaryColor,
      footerText,
      welcomeMessage,
      completedMessage,
      coverTitle,
      coverSubtitle,
      introHeading,
      introBody,
      designationsText,
    }),
    [
      productName,
      logoUrl,
      primaryColor,
      footerText,
      welcomeMessage,
      completedMessage,
      coverTitle,
      coverSubtitle,
      introHeading,
      introBody,
      designationsText,
    ]
  );

  const isDirty = savedBaseline !== null && buildSnapshot(currentFields) !== savedBaseline;

  const handleLogoFile = useCallback(async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setLogoUploading(true);
    try {
      const url = await uploadCompanyLogo(file);
      if (!url) throw new Error("Upload finished but no image URL was returned. Try again.");
      setLogoUrl(url);
      setLogoBroken(false);
      toast.success("Logo ready — save branding to publish");
    } catch (err) {
      toast.error(formatLogoUploadError(err, { fallback: "Could not upload logo" }));
    } finally {
      setLogoUploading(false);
    }
  }, []);

  const applyAiSuggestion = useCallback(
    async (productOverride, { silent = false, scope = "all" } = {}) => {
      setGeneratingCopy(true);
      try {
        const payload = {
          product_display_name: (productOverride || productName || "").trim(),
          context: aiBrief.trim(),
          scope,
        };
        payload.current_copy = {
          welcome_message: welcomeMessage.trim(),
          completed_message: completedMessage.trim(),
          report_config: {
            report_cover_title: coverTitle.trim(),
            report_cover_subtitle: coverSubtitle.trim(),
            report_intro_heading: introHeading.trim(),
            report_intro_body: introBody.trim(),
          },
        };
        const before = {
          welcomeMessage: welcomeMessage.trim(),
          completedMessage: completedMessage.trim(),
          coverTitle: coverTitle.trim(),
          coverSubtitle: coverSubtitle.trim(),
          introHeading: introHeading.trim(),
          introBody: introBody.trim(),
        };
        const res = await generateWorkspaceCopy(payload);
        const s = res.data?.suggestion || {};
        const rc = s.report_config || {};
        const touchParticipant = scope === "all" || scope === "participant";
        const touchPdf = scope === "all" || scope === "pdf";
        const next = { ...before };
        if (touchParticipant) {
          if (typeof s.welcome_message === "string" && s.welcome_message.trim()) {
            next.welcomeMessage = s.welcome_message.trim();
            setWelcomeMessage(next.welcomeMessage);
          }
          if (typeof s.completed_message === "string" && s.completed_message.trim()) {
            next.completedMessage = s.completed_message.trim();
            setCompletedMessage(next.completedMessage);
          }
        }
        if (touchPdf) {
          if (typeof rc.report_cover_title === "string" && rc.report_cover_title.trim()) {
            next.coverTitle = rc.report_cover_title.trim();
            setCoverTitle(next.coverTitle);
          }
          if (typeof rc.report_cover_subtitle === "string" && rc.report_cover_subtitle.trim()) {
            next.coverSubtitle = rc.report_cover_subtitle.trim();
            setCoverSubtitle(next.coverSubtitle);
          }
          if (typeof rc.report_intro_heading === "string" && rc.report_intro_heading.trim()) {
            next.introHeading = rc.report_intro_heading.trim();
            setIntroHeading(next.introHeading);
          }
          if (typeof rc.report_intro_body === "string" && rc.report_intro_body.trim()) {
            next.introBody = rc.report_intro_body.trim();
            setIntroBody(next.introBody);
          }
        }
        if (!silent) {
          if (scope === "participant") {
            setActivePanel("copy");
          } else if (scope === "pdf") {
            setActivePanel("reportIntro");
          }
        }
        const changed = Object.keys(before).some((key) => before[key] !== next[key]);
        if (!silent) {
          const bit =
            scope === "participant"
              ? "Participant messages"
              : scope === "pdf"
                ? "PDF cover & intro"
                : "Copy";
          if (changed) {
            toast.success(`${bit} updated in draft — save to publish`);
          } else {
            toast.error(
              "Copy did not change — open Participant messages or PDF cover & intro, or try different context"
            );
          }
        }
      } catch (err) {
        if (!silent) toast.error(err.message || "Could not generate copy");
      } finally {
        setGeneratingCopy(false);
      }
    },
    [
      productName,
      aiBrief,
      welcomeMessage,
      completedMessage,
      coverTitle,
      coverSubtitle,
      introHeading,
      introBody,
    ]
  );

  const manualAiScope =
    activePanel === "copy" ? "participant" : activePanel === "reportIntro" ? "pdf" : null;

  const handleManualGenerate = () => {
    if (!manualAiScope) {
      toast.error("Open Participant messages or PDF cover & intro, then click Generate");
      return;
    }
    applyAiSuggestion(undefined, { scope: manualAiScope });
  };

  const aiScopeHint =
    manualAiScope === "participant"
      ? "Updates welcome & completed messages for this tab only"
      : manualAiScope === "pdf"
        ? "Updates PDF cover & intro for this tab only"
        : activePanel === "designations"
          ? "Job titles are manual — open Participant messages or PDF cover & intro for AI"
          : "Open Participant messages or PDF cover & intro — Generate updates that tab only";

  const handleSave = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setSaving(true);
    try {
      const designationLabels = designationsText
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean);
      await updateWorkspace({
        product_display_name: productName.trim(),
        logo_url: logoUrl.trim(),
        primary_color: primaryColor.trim(),
        footer_text: footerText.trim(),
        welcome_message: welcomeMessage.trim(),
        completed_message: completedMessage.trim(),
        report_config: {
          report_cover_title: coverTitle.trim() || null,
          report_cover_subtitle: coverSubtitle.trim() || null,
          report_intro_heading: introHeading.trim() || null,
          report_intro_body: introBody.trim() || null,
        },
        designations: designationLabels.map((label, i) => ({
          label,
          sort_order: i,
        })),
      });
      await refreshAdminSession();
      setSavedBaseline(buildSnapshot(currentFields));
      toast.success("Saved — live for participants and PDFs");
      onSaved?.();
    } catch (err) {
      toast.error(err.message || "Could not save branding");
    } finally {
      setSaving(false);
    }
  };

  if (isPlatformAdmin || !sessionHasTenant || (!loading && !workspaceReady)) {
    return null;
  }

  if (loading && !workspaceReady) {
    return null;
  }

  const colorPreview = hexForColorInput(primaryColor);

  const aiBriefField = (
    <div className="mt-3 space-y-1.5">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        AI copy assistant
      </p>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
        <label className="flex-1 min-w-0 flex items-center gap-2">
          <span className="sr-only">Context for AI (optional)</span>
          <input
            type="text"
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white"
            value={aiBrief}
            onChange={(e) => setAiBrief(e.target.value)}
            disabled={loading || saving || generatingCopy}
            maxLength={500}
            placeholder="Optional context for AI (tone, audience…)"
          />
        </label>
        <button
          type="button"
          onClick={handleManualGenerate}
          disabled={generatingCopy || saving}
          title={
            manualAiScope === "participant"
              ? "Participant messages"
              : manualAiScope === "pdf"
                ? "PDF cover & intro"
                : undefined
          }
          className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg bg-[#004f73] text-white px-4 py-2 text-sm font-semibold disabled:opacity-50 whitespace-nowrap"
        >
          <Sparkles className="h-4 w-4" aria-hidden />
          {generatingCopy ? "Generating…" : "Generate with AI"}
        </button>
      </div>
      <p className="text-[11px] text-slate-500 leading-snug">{aiScopeHint}</p>
    </div>
  );

  return (
    <section
      id="workspace-branding"
      className="rounded-2xl border border-slate-200 bg-white p-5 mb-0 shadow-sm scroll-mt-24 w-full max-w-none box-border"
    >
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-base font-semibold text-slate-900">Workspace branding</h2>
          <p className="text-sm text-slate-500 mt-0.5 max-w-3xl">
            Generated copy stays in this form until you choose Save branding.
          </p>
        </div>
        <button
          type="button"
          onClick={() => handleSave()}
          disabled={loading || saving || logoUploading || !productName.trim() || !isDirty}
          className="rounded-xl bg-[#004f73] text-white text-sm font-semibold px-5 py-2.5 disabled:opacity-50 shrink-0"
        >
          {saving ? "Saving…" : "Save branding"}
        </button>
      </div>

      {isDirty ? (
        <div
          className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950"
          role="status"
        >
          <p className="font-semibold">Draft</p>
          <p className="text-xs text-amber-900/80 mt-0.5">
            Unsaved changes to name, logo, colors, or messaging — save to publish.
          </p>
        </div>
      ) : null}

      <form onSubmit={handleSave} className="space-y-5">
        <div className="space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Core branding</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm">
              <span className="font-medium text-slate-600 mb-1 block">Product display name</span>
              <input
                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm"
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
                disabled={loading || saving}
                maxLength={120}
                required
              />
            </label>
            <div className="block text-sm sm:col-span-2">
              <span className="font-medium text-slate-600 mb-1 block">Workspace logo</span>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                <div className="h-20 w-20 shrink-0 rounded-xl border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center">
                  {logoUrl.trim() && !logoBroken ? (
                    <img
                      src={logoUrl.trim()}
                      alt=""
                      className="h-full w-full object-contain p-1.5"
                      onError={() => setLogoBroken(true)}
                    />
                  ) : (
                    <span className="text-[10px] text-slate-400 text-center px-2 leading-tight">
                      {logoBroken ? "Preview unavailable" : "No logo"}
                    </span>
                  )}
                </div>
                <div className="min-w-0 flex-1 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      disabled={loading || saving || logoUploading}
                      onClick={() => document.getElementById("workspace-branding-logo-input")?.click()}
                      className="inline-flex items-center rounded-lg border border-[#004f73]/25 bg-[#004f73]/5 px-3 py-1.5 text-xs font-medium text-[#004f73] hover:bg-[#004f73]/10 disabled:opacity-50"
                    >
                      {logoUploading ? "Uploading…" : logoUrl.trim() ? "Replace image" : "Upload image"}
                    </button>
                    {logoUrl.trim() ? (
                      <button
                        type="button"
                        disabled={loading || saving || logoUploading}
                        onClick={() => {
                          setLogoUrl("");
                          setLogoBroken(false);
                        }}
                        className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                      >
                        Remove
                      </button>
                    ) : null}
                    <input
                      id="workspace-branding-logo-input"
                      type="file"
                      className="hidden"
                      accept={LOGO_ACCEPT}
                      onChange={handleLogoFile}
                    />
                  </div>
                  <label className="block">
                    <span className="text-[11px] text-slate-500 mb-1 block">
                      Or paste an image URL
                    </span>
                    <input
                      className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm"
                      value={logoUrl}
                      onChange={(e) => {
                        setLogoUrl(e.target.value);
                        setLogoBroken(false);
                      }}
                      disabled={loading || saving || logoUploading}
                      placeholder="https://… or /path/to/logo.png"
                      spellCheck={false}
                    />
                  </label>
                  <p className="text-[11px] text-slate-400 leading-snug">
                    PNG, JPG, or GIF · max 5 MB. WebP/SVG are not supported for upload — use a URL
                    instead if needed. Logo appears in admin chrome and participant pages.
                  </p>
                </div>
              </div>
            </div>
            <label className="block text-sm">
              <span className="font-medium text-slate-600 mb-1 block">Primary color</span>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={colorPreview}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  disabled={loading || saving}
                  aria-label="Pick primary color"
                  className="h-10 w-10 shrink-0 cursor-pointer rounded-lg border border-slate-200 bg-white p-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <input
                  className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm font-mono"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  onBlur={(e) => {
                    const normalized = normalizeHexInput(e.target.value);
                    if (/^#[0-9a-f]{6}$/.test(normalized)) {
                      setPrimaryColor(normalized);
                    }
                  }}
                  disabled={loading || saving}
                  placeholder="#004f73"
                  maxLength={7}
                  spellCheck={false}
                />
              </div>
            </label>
            <label className="block text-sm">
              <span className="font-medium text-slate-600 mb-1 block">PDF footer text</span>
              <input
                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm"
                value={footerText}
                onChange={(e) => setFooterText(e.target.value)}
                disabled={loading || saving}
                maxLength={240}
                placeholder="e.g. Acme Corp | Confidential"
              />
              <span className="text-[11px] text-slate-400 mt-1 block">
                Shown at the bottom of generated PDF reports
              </span>
            </label>
          </div>
        </div>

        <div className="pt-1 border-t border-slate-100">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
            Additional customization
          </p>
          <div className="grid gap-3 sm:grid-cols-3">
            {CUSTOMIZATION_PANELS.map(({ key, title, description, Icon }) => {
              const isOpen = activePanel === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setActivePanel(isOpen ? null : key)}
                  aria-expanded={isOpen}
                  className={`group w-full text-left rounded-xl border p-3.5 transition-colors ${
                    isOpen
                      ? "border-[#004f73]/35 bg-[#004f73]/[0.04] ring-1 ring-[#004f73]/15"
                      : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span
                      className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                        isOpen ? "bg-[#004f73]/10 text-[#004f73]" : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                    </span>
                    <ChevronDown
                      className={`h-4 w-4 shrink-0 text-slate-400 ${isOpen ? "rotate-180 text-[#004f73]" : ""}`}
                    />
                  </div>
                  <p className="mt-2 text-sm font-semibold text-slate-900">{title}</p>
                  <p className="mt-1 text-xs text-slate-500">{description}</p>
                </button>
              );
            })}
          </div>

          {aiBriefField}

          {activePanel === "reportIntro" ? (
            <div className="mt-3">
              <div className="grid gap-3 sm:grid-cols-2 rounded-xl border border-slate-200 bg-slate-50/70 p-4">
                <label className="block text-sm">
                  <span className="font-medium text-slate-600 mb-1 block">Cover title</span>
                  <input
                    className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-white"
                    value={coverTitle}
                    onChange={(e) => setCoverTitle(e.target.value)}
                    disabled={loading || saving}
                    maxLength={160}
                  />
                </label>
                <label className="block text-sm">
                  <span className="font-medium text-slate-600 mb-1 block">Cover subtitle</span>
                  <input
                    className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-white"
                    value={coverSubtitle}
                    onChange={(e) => setCoverSubtitle(e.target.value)}
                    disabled={loading || saving}
                    maxLength={240}
                  />
                </label>
                <label className="block text-sm sm:col-span-2">
                  <span className="font-medium text-slate-600 mb-1 block">Intro heading</span>
                  <input
                    className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-white"
                    value={introHeading}
                    onChange={(e) => setIntroHeading(e.target.value)}
                    disabled={loading || saving}
                    maxLength={160}
                  />
                </label>
                <label className="block text-sm sm:col-span-2">
                  <span className="font-medium text-slate-600 mb-1 block">Intro body</span>
                  <textarea
                    className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm min-h-[96px] bg-white"
                    value={introBody}
                    onChange={(e) => setIntroBody(e.target.value)}
                    disabled={loading || saving}
                  />
                </label>
              </div>
            </div>
          ) : null}

          {activePanel === "designations" ? (
            <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50/70 p-4">
              <textarea
                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm min-h-[120px] font-mono bg-white"
                value={designationsText}
                onChange={(e) => setDesignationsText(e.target.value)}
                disabled={loading || saving}
                placeholder={"Executive\nLeadership Team\nManager\nContributor\nOther"}
              />
            </div>
          ) : null}

          {activePanel === "copy" ? (
            <div className="mt-3">
              <div className="grid gap-3 rounded-xl border border-slate-200 bg-slate-50/70 p-4">
                <label className="block text-sm">
                  <span className="font-medium text-slate-600 mb-1 block">Welcome message</span>
                  <textarea
                    className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm min-h-[72px] bg-white"
                    value={welcomeMessage}
                    onChange={(e) => setWelcomeMessage(e.target.value)}
                    disabled={loading || saving}
                  />
                </label>
                <label className="block text-sm">
                  <span className="font-medium text-slate-600 mb-1 block">Completed message</span>
                  <textarea
                    className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm min-h-[56px] bg-white"
                    value={completedMessage}
                    onChange={(e) => setCompletedMessage(e.target.value)}
                    disabled={loading || saving}
                  />
                </label>
              </div>
            </div>
          ) : null}
        </div>

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between pt-4 border-t border-slate-100">
          <p className="text-xs text-slate-500">
            {isDirty ? "Save to publish messaging and PDF copy." : "All saved settings are live."}
          </p>
          <button
            type="submit"
            disabled={loading || saving || logoUploading || !productName.trim() || !isDirty}
            className="rounded-xl bg-[#004f73] text-white text-sm font-semibold px-5 py-2.5 disabled:opacity-50 shrink-0"
          >
            {saving ? "Saving…" : "Save branding"}
          </button>
        </div>
      </form>
    </section>
  );
}
